/**
 * MijnCalendlyPage — Een projectmanager (of admin) kan hier zijn eigen
 * Calendly URL beheren. Zijn directe team-leden (partner → teamleider → agent → affiliate)
 * krijgen deze URL via /api/platform/intake/calendly ipv de globale URL.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Loader2, Calendar, ExternalLink, Info, RotateCcw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function MijnCalendlyPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [url, setUrl] = useState('');
  const [globalUrl, setGlobalUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/platform/my-calendly`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUrl(res.data?.calendly_url || '');
        setGlobalUrl(res.data?.global_calendly_url || '');
        setEnabled(res.data?.calendly_enabled !== false);
      } catch (e) {
        if (e.response?.status === 403) {
          toast.error('Alleen projectmanagers en admins kunnen een eigen Calendly-link beheren');
        } else {
          toast.error('Kon Calendly-instellingen niet laden');
        }
      }
      setLoading(false);
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${BACKEND}/api/platform/my-calendly`,
        { calendly_url: url || '' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUrl(res.data?.calendly_url || '');
      toast.success(url ? 'Calendly-link opgeslagen' : 'Eigen Calendly-link verwijderd — uw team gebruikt nu de globale link');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  const clear = async () => {
    if (!window.confirm('Eigen Calendly-link verwijderen? Uw team gebruikt dan de globale IMPERIA AI link.')) return;
    setUrl('');
    setSaving(true);
    try {
      await axios.patch(
        `${BACKEND}/api/platform/my-calendly`,
        { calendly_url: '' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Eigen Calendly-link verwijderd');
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ padding: 40, color: '#94a3b8' }}><Loader2 size={16} className="animate-spin" /> Laden…</div>;
  }

  const inheritsFromGlobal = !url && globalUrl;

  return (
    <div style={{ padding: 24, color: 'white', maxWidth: 720 }} data-testid="mijn-calendly-page">
      <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Mijn Calendly-link</h1>
      <p style={{ color: '#94a3b8', marginTop: 4, marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
        Stel hier uw eigen Calendly-link in. Iedereen onder u in de hiërarchie (partners, teamleiders, agents, affiliates die u heeft aangeworven)
        krijgt dan automatisch ÚW link voor intake-gesprekken — in plaats van de globale Calendly-link.
      </p>

      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ background: '#D4A017', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={18} color="#0f172a" />
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Persoonlijke Calendly URL</h2>
            <p style={{ color: '#94a3b8', fontSize: 12, margin: '2px 0 0' }}>Laat leeg om de globale Calendly-link te gebruiken</p>
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Calendly URL
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="mijn-calendly-url"
          placeholder="https://calendly.com/uw-eigen-account/intake"
          style={{
            width: '100%',
            background: '#0f172a',
            color: 'white',
            border: '1px solid #1e293b',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 14,
          }}
        />
        <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Moet beginnen met https:// en calendly.com bevatten.</p>

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#D4A017', fontSize: 13, marginTop: 14 }}
          >
            <ExternalLink size={12} /> Test deze link in nieuw tabblad
          </a>
        )}
      </div>

      {inheritsFromGlobal && (
        <div style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.3)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', gap: 10, fontSize: 13, color: '#fde68a' }}>
          <Info size={18} color="#D4A017" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>U heeft (nog) geen eigen Calendly-link.</strong>
            <p style={{ margin: '4px 0 0', color: '#fde68a99', lineHeight: 1.5 }}>
              Uw team krijgt op dit moment de globale Calendly-link: <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{globalUrl}</code>
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={save}
          disabled={saving}
          data-testid="mijn-calendly-save"
          style={{
            background: '#D4A017',
            color: '#0f172a',
            fontWeight: 700,
            padding: '12px 24px',
            borderRadius: 10,
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
        </button>
        {url && (
          <button
            onClick={clear}
            disabled={saving}
            data-testid="mijn-calendly-clear"
            style={{
              background: 'transparent',
              color: '#fca5a5',
              fontWeight: 700,
              padding: '12px 16px',
              borderRadius: 10,
              border: '1px solid rgba(239,68,68,0.3)',
              cursor: saving ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RotateCcw size={14} /> Terug naar globale link
          </button>
        )}
      </div>
    </div>
  );
}
